import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  isLikelyDuplicateEmailSignUpResponse,
  isSignUpEmailAlreadyRegistered
} from '../lib/authErrors';
import { AuthEyeIcon, AuthEyeSlashIcon } from '../components/AuthPasswordVisibilityIcons';
import './AuthPages.css';

const Signup: React.FC = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = (searchParams.get('email') || '').trim();

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountAlreadyExists, setAccountAlreadyExists] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (emailFromUrl) {
      setEmail(emailFromUrl);
      requestAnimationFrame(() => {
        document.getElementById('signup-password')?.focus();
      });
    }
  }, [emailFromUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAccountAlreadyExists(false);
    setSuccess(null);

    const emailTrim = email.trim();
    if (!emailTrim) {
      setError('Please enter your email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: signUpError } = await signUp(emailTrim, password);
      if (signUpError) {
        if (isSignUpEmailAlreadyRegistered(signUpError)) {
          setAccountAlreadyExists(true);
        } else {
          setError(signUpError.message);
        }
        return;
      }
      if (data.session) {
        navigate('/', { replace: true });
        return;
      }
      // Duplicate email + "confirm email" enabled: Supabase returns no error (see authErrors.ts).
      if (isLikelyDuplicateEmailSignUpResponse(data.user ?? null, data.session)) {
        setAccountAlreadyExists(true);
        return;
      }
      setSuccess(
        'Check your email to confirm your account if required. You can then log in.'
      );
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Create account</h1>
        <p className="auth-subtitle">
          {emailFromUrl
            ? 'Your email is filled in below—choose a password to finish creating your account.'
            : 'Sign up with email and password to sync your MBA profile.'}
        </p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {accountAlreadyExists && (
            <div className="auth-message error" role="alert">
              <p className="auth-error-title">An account with this email already exists.</p>
              <p className="auth-error-hint">
                <Link to="/login">Log in</Link>
                {' · '}
                <Link
                  to={`/forgot-password${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`}
                >
                  Reset your password
                </Link>
              </p>
            </div>
          )}
          {error && !accountAlreadyExists && (
            <div className="auth-message error" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="auth-message success" role="status">
              {success}{' '}
              <Link to="/login">Go to log in</Link>
            </div>
          )}
          <div className="auth-field">
            <label htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              aria-required="true"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="signup-password">Password</label>
            <div className="auth-password-wrap">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                aria-required="true"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <AuthEyeSlashIcon /> : <AuthEyeIcon />}
              </button>
            </div>
          </div>
          <button type="submit" className="auth-submit" disabled={submitting || !!success}>
            {submitting ? 'Creating account…' : 'Sign up'}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
